import { useBoolean, useInterval } from 'ahooks';
import { Button, Space } from 'antd';
import Animate from 'rc-animate';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AccountSignUpPayload } from 'api/account';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Loading } from 'components/atoms/loading';
import { toastService } from 'components/molecules/toast';
import { Caption01 } from 'components/styles';
import { API_HOST } from 'config/environment';
import { ROCKETLANE_PRIVACY } from 'constant';
import { useTranslate } from 'hooks/use-translation';
import {
  useAccountSignUpMutation,
  useIsDomainExistsMutation,
} from 'queries/account';
import { getCurrentUserTz } from 'utils/timezone';
import {
  companyNameToDomainName,
  FullNameRegex,
  MobileNumberRegex,
  SubDomainRegex,
} from './util';

const Terms = styled.div`
  ${Caption01};
  text-align: center;
`;

const LoadingContainer = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: var(--zindex-popover-high);
  background-color: var(--white-90);
`;

const MessageContainer = styled.h2`
  padding: var(--spacing-08);
`;

interface Props {
  token: string;
  emailId: string;
}

const DOMAIN_SUFFIX = API_HOST?.includes('nitro')
  ? '.nitro.run'
  : '.rocketlane.com';

const getDomainName = (subDomain: string) => {
  return subDomain.toLowerCase() + DOMAIN_SUFFIX;
};

const getFirstAndLastName = (fullName: string) => {
  const [firstName, ...rest] = fullName.split(' ');

  return {
    firstName,
    lastName: rest.join(' '),
  };
};

type FormValues = Omit<AccountSignUpPayload, 'firstName' | 'lastName'> & {
  fullName: string;
};

const commonEmailTLDs = [
  'yahoo.com',
  'gmail.com',
  'google.com',
  'hotmail.com',
  'me.com',
  'aol.com',
  'mac.com',
  'live.com',
  'googlemail.com',
  'msn.com',
  'hotmail.co.uk',
  'yahoo.co.uk',
  'facebook.com',
  'gmz.com',
  'mail.com',
  'proton',
  'hey',
];

const LoadingOverlay: FC = () => {
  const messages = [
    <FormattedMessage id="account_setup.loading.setting_up" />,
    <FormattedMessage id="account_setup.loading.sealing" />,
    <FormattedMessage id="account_setup.loading.fuel_level" />,
    <FormattedMessage id="account_setup.loading.seat_belts" />,
    <FormattedMessage id="account_setup.loading.engine_on" />,
    <FormattedMessage id="account_setup.loading.launching" />,
  ];

  const lastIndex = messages.length - 1;
  const [index, setIndex] = useState(0);

  useInterval(() => {
    if (index < lastIndex) {
      setIndex(index + 1);
    }
  }, 1500);

  return (
    <LoadingContainer>
      <Loading size="large" />
      <MessageContainer>{messages[index]}</MessageContainer>
    </LoadingContainer>
  );
};

export const AccountSetupForm: FC<Props> = ({ token, emailId }) => {
  const translate = useTranslate();
  const { mutate: completeSignUp } = useAccountSignUpMutation();
  const { mutateAsync: isDomainExistsAsync, isLoading } =
    useIsDomainExistsMutation();
  const [showLoader, { setTrue: showLoading, setFalse: hideLoading }] =
    useBoolean(false);
  const location = useLocation();
  const mandatePhoneNumber = new URLSearchParams(location.search).get(
    'mandatePhoneNumber',
  );

  const [form] = Form.useForm();

  let domainFromEmail = emailId.split('@')[1].split('.')[0];

  domainFromEmail = commonEmailTLDs.find((item) =>
    item.includes(domainFromEmail.toLowerCase()),
  )
    ? ''
    : domainFromEmail;

  const handleOnFinish = (values) => {
    const { domainName, fullName, ...rest } = values as FormValues;
    const errorTitle = translate('failure');
    const errorMessage = translate('error');

    showLoading();

    completeSignUp(
      {
        ...rest,
        ...getFirstAndLastName(fullName),
        domainName: getDomainName(domainName),
        timeZone: getCurrentUserTz(),
      },
      {
        onError: () => {
          hideLoading();
          toastService({
            title: errorTitle,
            message: errorMessage,
          }).error();
        },
      },
    );
  };

  const handleOnValuesChange = (changed, _) => {
    const { companyName } = changed;

    if (companyName) {
      form.setFieldsValue({
        domainName: companyNameToDomainName(companyName),
      });
      form.validateFields(['domainName']);
    }
  };

  return (
    <>
      {showLoader ? (
        <Animate transitionName="fade" transitionAppear>
          <LoadingOverlay />
        </Animate>
      ) : null}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleOnFinish}
        onValuesChange={handleOnValuesChange}
        initialValues={{ token, emailId }}
      >
        <Form.Item name="token" hidden />
        <Form.Item name="emailId" hidden />
        <Form.Item
          name="fullName"
          label={<FormattedMessage id="account_setup.form.full_name" />}
          required
          rules={[
            {
              validator: (_, value: string) => {
                if (FullNameRegex.test(value)) {
                  return Promise.resolve();
                }

                return Promise.reject(
                  new Error(translate('account_setup.form.full_name.error')),
                );
              },
            },
          ]}
        >
          <Input autoFocus placeholder="John Doe" />
        </Form.Item>
        <Form.Item
          name="companyName"
          label={<FormattedMessage id="account_setup.form.company_name" />}
          required
          initialValue={domainFromEmail}
          rules={[
            {
              required: true,
              message: 'Company name is required',
            },
            {
              whitespace: true,
              message: 'Company name cannot be empty',
            },
            {
              min: 2,
              message: 'Company name must be at least 2 characters',
            },
          ]}
        >
          <Input autoComplete="false" placeholder="Acme" />
        </Form.Item>
        <Form.Item
          name="domainName"
          label={<FormattedMessage id="account_setup.form.subdomain" />}
          required
          hasFeedback
          tooltip={
            <FormattedMessage id="account_setup.form.subdomain.helper" />
          }
          initialValue={domainFromEmail}
          rules={[
            {
              required: true,
              message: 'Domain name is required',
            },
            {
              whitespace: true,
              message: 'Domain name cannot be empty',
            },
            {
              min: 2,
              message: 'Domain name must be at least 2 characters',
            },
            {
              validator: async (_, value: string) => {
                /**
                 * Skipping validation for the cases:
                 * 1. when URL and domain from email are same
                 * 2. AND domainName inputValue is same as email domain
                 */
                const skipCheck =
                  window.location.hostname.split('.')[0] === domainFromEmail &&
                  value === domainFromEmail;

                if (skipCheck) {
                  return Promise.resolve();
                }

                if (SubDomainRegex.test(value)) {
                  const response = await isDomainExistsAsync({
                    token,
                    domainName: getDomainName(value),
                  });

                  if (response.domainExists) {
                    return Promise.reject(
                      new Error(
                        translate('account_setup.form.subdomain.exists'),
                      ),
                    );
                  }

                  return Promise.resolve();
                }

                return Promise.reject(
                  translate('account_setup.form.subdomain.error'),
                );
              },
            },
          ]}
        >
          <Input autoComplete="off" suffix={DOMAIN_SUFFIX} />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label={<FormattedMessage id="account_setup.form.password" />}
          required
          rules={[
            {
              required: true,
              message: 'Password is required',
            },
            {
              whitespace: true,
              message: 'Password cannot be empty',
            },
            {
              min: 8,
              message: 'Password must be at least 8 characters',
            },
          ]}
        >
          <Input autoComplete="off" type="password" />
        </Form.Item>
        <Form.Item
          name="contactNumber"
          label={<FormattedMessage id="account_setup.form.phone" />}
          rules={[
            {
              validator: (_, value: string) => {
                if (value) {
                  if (MobileNumberRegex.test(value)) {
                    return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error(translate('account_setup.form.phone.error')),
                  );
                }

                return Promise.resolve();
              },
            },
            {
              required: mandatePhoneNumber === 'true',
              message: 'Phone number is required',
            },
          ]}
        >
          <Input placeholder="+1 123 456 7890" />
        </Form.Item>
        <Space direction="vertical" size={12}>
          <Button
            type="primary"
            block
            htmlType="submit"
            loading={isLoading}
            disabled={isLoading}
          >
            {translate('account_setup.form.start_my_trial')}
          </Button>
          <Terms>
            <FormattedMessage
              id="account_setup.terms"
              values={{
                a: (chunks) => (
                  <a href={ROCKETLANE_PRIVACY} target="_blank" rel="noreferrer">
                    {chunks}
                  </a>
                ),
              }}
            />
          </Terms>
        </Space>
      </Form>
    </>
  );
};
